import { LowSync } from "lowdb";
import { LocalStorage } from "lowdb/browser";
import { isPlainObject, merge, unionBy } from "lodash";
import {
  ENV_NAME,
  ENV_HOST,
  PROXY_HOST,
  PROXY_ENABLED,
  PROXY_EXTERNAL_BUCKETS,
  PROXY_INTERNAL_BUCKETS,
  ENV_INTERNAL_TOKEN,
  ENV_INTERNAL_PROJECT,
} from "utilities/constants";

export const parseEnv = (envVar) => {
  if (["True", "true", "1", "yes", "Yes"].includes(envVar)) {
    return true;
  } else if (["False", "false", "0", "no", "No"].includes(envVar)) {
    return false;
  } else if (envVar === "") {
    return undefined;
  } else {
    return envVar;
  }
};

const ENVIRONMENTS = [
  {
    [ENV_NAME]: window._env_?.WEBAPP_ENV,
    [ENV_HOST]: window._env_?.WEBAPP_API_HOST,
    [PROXY_HOST]: window._env_?.FILES_PROXY_HOST,
    [PROXY_ENABLED]: parseEnv(window._env_?.FILES_PROXY_ENABLED),
    [PROXY_EXTERNAL_BUCKETS]: parseEnv(window._env_?.FILES_PROXY_EXTERNAL_BUCKETS),
    [PROXY_INTERNAL_BUCKETS]: parseEnv(window._env_?.FILES_PROXY_INTERNAL_BUCKETS),
    [ENV_INTERNAL_TOKEN]: window._env_?.WEBAPP_INTERNAL_TOKEN,
    [ENV_INTERNAL_PROJECT]: window._env_?.WEBAPP_INTERNAL_PROJECT,
  },
];

const DEFAULT_LOCALSTORAGE_KEY = "UBIOPS_ENVIRONMENT_MANAGER";
const DEFAULTS = {
  current: window._env_?.WEBAPP_ENV,
  environments: [],
};

const HTTP_REGEX = /^https?:\/\//;
const isHttpUrl = (str) => HTTP_REGEX.test(str);
const isNonEmptyString = (str) => typeof str === "string" && str.length > 0;

class EnvironmentManager {
  constructor(key = DEFAULT_LOCALSTORAGE_KEY) {
    this.db = new LowSync(new LocalStorage(key), DEFAULTS);
    this.db.read();

    this.db.update((data) => {
      data.environments = unionBy(data.environments, ENVIRONMENTS, "name");
      return data;
    });

    this.db.update((data) => {
      ENVIRONMENTS.forEach((env) => {
        data.environments = data.environments.map((e) =>
          e.name === env.name ? merge(e, env) : e
        );
      });
      return data;
    });

    if (!this.current()) {
      console.error("Invalid EnvironmentManager State", this.db.data); // eslint-disable-line no-console
    }
  }

  __current() {
    return this.getEnvironment(this.db.data.current);
  }

  current() {
    return this.__current();
  }

  set(key, value) {
    if (key === "name") {
      throw new Error("Setting Current Environment Name Not Allowed");
    }
    this.__current()[key] = value;
    this.db.write();
  }

  unset(key) {
    if (key === "name") {
      throw new Error("Unsetting Current Environment Name Not Allowed");
    }
    this.__current()[key] = null;
    this.db.write();
  }

  get(key) {
    return this.__current()[key];
  }

  isValidEnvironment(env) {
    return (
      isPlainObject(env) && isNonEmptyString(env.name) && isHttpUrl(env.host)
    );
  }

  createEnvironment(newEnv) {
    if (!this.isValidEnvironment(newEnv)) {
      throw new Error("Invalid Environment Object");
    }

    if (this.hasEnvironment(newEnv.name)) {
      throw new Error("Environment Already Exists");
    }

    this.db.update((data) => {
      data.environments.push(newEnv);
      return data;
    });

    return newEnv;
  }

  removeEnvironment(name) {
    if (name === this.current()?.name) {
      throw new Error("Cannot Remove Current Environment");
    }
    this.db.update((data) => {
      data.environments = data.environments.filter((env) => env.name !== name);
      return data;
    });
  }

  getEnvironment(name) {
    return this.db.data.environments.find((env) => env.name === name);
  }

  hasEnvironment(name) {
    return !!this.getEnvironment(name);
  }

  setEnvironmentAsCurrent(key) {
    if (!this.hasEnvironment(key)) {
      throw new Error("Environment Not Found");
    }
    this.db.update((data) => {
      data.current = key;
      return data;
    });
  }

  dump() {
    console.log("EnvironmentManager", this.db.data); // eslint-disable-line no-console
  }
}

const EnvironmentManagerInstance = new EnvironmentManager();

export default EnvironmentManagerInstance;
